import { memo } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';
import {
  formatCurrency,
  getCurrencySymbol,
  DEFAULT_CURRENCY,
  CurrencyCode,
} from '../../utils/currency';

import { Campaign } from '../../types/campaign';

export interface CampaignCardProps {
  campaign: Campaign & { isExternal?: boolean; externalFormUrl?: string };
  onApply?: (campaignId: string) => void;
  onApplyExternal?: (campaign: any) => void;
  onViewDetails?: (campaign: Campaign) => void;
  isLoading?: boolean;
  onToggleCompare?: (campaign: Campaign) => void;
  isSelectedForComparison?: boolean;
  onViewApplications?: (campaignId: string) => void;
  isAgencyCampaign?: boolean;
}

const CampaignCard = ({
  campaign,
  onApply,
  onApplyExternal,
  onViewDetails,
  isLoading,
  onToggleCompare,
  isSelectedForComparison,
  onViewApplications,
  isAgencyCampaign,
}: CampaignCardProps) => {
  const isExternalCampaign =
    campaign.isExternal || campaign.externalFormUrl;

  const getStatusBadge = () => {
    if (!campaign.isApplied) return null;
    const statusColors: Record<
      'pending' | 'accepted' | 'rejected',
      string
    > = {
      pending:
        'bg-amber-500/10 text-amber-400 border-amber-500/20',
      accepted:
        'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      rejected: 'bg-white/[0.05] text-white/30 border-white/[0.08]',
    };
    const statusIcons: Record<
      'pending' | 'accepted' | 'rejected',
      string
    > = {
      pending: 'Clock',
      accepted: 'CheckCircle2',
      rejected: 'XCircle',
    };
    const status: 'pending' | 'accepted' | 'rejected' =
      campaign.applicationStatus === 'accepted'
        ? 'accepted'
        : campaign.applicationStatus === 'rejected'
        ? 'rejected'
        : 'pending';
    return (
      <span
        className={`px-3 py-1.5 rounded-full text-xs font-medium
          border flex items-center gap-1.5 backdrop-blur-sm
          ${statusColors[status]}`}
      >
        <Icon name={statusIcons[status]} size={11} />
        {status === 'pending' && 'Pending'}
        {status === 'accepted' && 'Accepted'}
        {status === 'rejected' && 'Not Selected'}
      </span>
    );
  };

  const getDeadlineInfo = () => {
    const src = campaign?.deadline || campaign?.endDate;
    if (!src)
      return { daysLeft: 0, formattedDate: 'N/A', isUrgent: false };
    const d = src instanceof Date ? src : new Date(src);
    if (isNaN(d.getTime()))
      return {
        daysLeft: 0,
        formattedDate: 'Invalid',
        isUrgent: false,
      };
    const daysLeft = Math.ceil(
      (d.getTime() - Date.now()) / 86400000
    );
    const formattedDate = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return { daysLeft, formattedDate, isUrgent: daysLeft <= 3 };
  };

  const { daysLeft, formattedDate, isUrgent } = getDeadlineInfo();
  const minViews = campaign.minViews || 0;
  const maxViews = campaign.maxViews || 0;
  const payPerView = campaign.payPerView || campaign.costPerView || 0;
  const currency: CurrencyCode = campaign.currency || DEFAULT_CURRENCY;
  const currencySymbol = getCurrencySymbol(currency);
  const estimatedMin = formatCurrency(minViews * payPerView, currency, {
    showSymbol: false,
  });
  const estimatedMax = formatCurrency(maxViews * payPerView, currency, {
    showSymbol: false,
  });
  const hasBrandLogo = Boolean(campaign.brandLogo || campaign.image);
  const brandLogoSrc = campaign.brandLogo || campaign.image;
  const displayBrandLogoAlt =
    campaign.brandLogoAlt ||
    campaign.alt ||
    `${campaign.brandName || campaign.title} logo`;
  const displayBrandName = campaign.brandName || campaign.title;

  const iconBtn =
    'w-8 h-8 rounded-xl bg-white/[0.06] border border-white/[0.08] ' +
    'text-white/40 hover:text-white hover:bg-white/[0.10] ' +
    'flex items-center justify-center transition-all duration-200';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border transition-all
        duration-300 group flex flex-col
        ${
          isSelectedForComparison
            ? 'border-primary/40 bg-gradient-to-br from-primary/[0.06] to-transparent'
            : 'border-white/[0.07] bg-gradient-to-br from-white/[0.04] to-transparent hover:border-white/[0.13]'
        }
        hover:shadow-2xl hover:shadow-black/30`}
    >
      {/* Top shimmer */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Comparison selected ring */}
      {isSelectedForComparison && (
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      )}

      {/* Action buttons — top-right */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
        {getStatusBadge()}
        {onToggleCompare && (
          <button
            type="button"
            aria-label={
              isSelectedForComparison
                ? 'Remove from comparison'
                : 'Add to comparison'
            }
            title={
              isSelectedForComparison
                ? 'Remove from comparison'
                : 'Add to comparison'
            }
            onClick={() => onToggleCompare(campaign)}
            className={`${iconBtn} ${
              isSelectedForComparison
                ? 'bg-primary/20 border-primary/30 text-primary'
                : ''
            }`}
          >
            <Icon name="GitCompare" size={14} />
          </button>
        )}
        <button
          type="button"
          aria-label="Share campaign"
          title="Share campaign"
          onClick={(e) => {
            e.stopPropagation();
            const url = `${window.location.origin}/c/${campaign.id}`;
            if (navigator.share) {
              navigator
                .share({
                  title: campaign.title,
                  text: `Check out: ${campaign.title}`,
                  url,
                })
                .catch(() => {});
            } else {
              navigator.clipboard.writeText(url);
              const t = document.createElement('div');
              t.className =
                'fixed bottom-4 left-1/2 -translate-x-1/2 ' +
                'bg-white text-gray-900 px-4 py-2 rounded-lg shadow-lg z-50';
              t.textContent = '✅ Link copied!';
              document.body.appendChild(t);
              setTimeout(() => t.remove(), 2000);
            }
          }}
          className={iconBtn}
        >
          <Icon name="Share2" size={14} />
        </button>
        {onViewDetails && (
          <button
            type="button"
            aria-label="Quick view"
            title="Quick view"
            onClick={() => onViewDetails(campaign)}
            className={iconBtn}
          >
            <Icon name="Eye" size={14} />
          </button>
        )}
      </div>

      {/* Card body */}
      <div className="p-5 space-y-4 flex-1 flex flex-col">
        {/* Brand header */}
        <div className="flex items-start gap-3.5 pr-28">
          <div className="relative flex-shrink-0">
            <div
              className="w-12 h-12 rounded-xl overflow-hidden
                bg-white/[0.06] border border-white/[0.10]
                flex items-center justify-center"
            >
              {hasBrandLogo ? (
                <Image
                  src={brandLogoSrc}
                  alt={displayBrandLogoAlt}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon
                  name="Building2"
                  size={20}
                  className="text-white/25"
                />
              )}
            </div>
            {/* subtle glow behind logo on hover */}
            <div className="absolute inset-0 rounded-xl bg-primary/10 blur-md opacity-0 group-hover:opacity-60 transition-opacity duration-500 -z-10" />
          </div>

          <div className="flex-1 min-w-0 pt-0.5">
            <h3
              className="font-semibold text-sm text-white
                line-clamp-1 leading-snug mb-1
                group-hover:text-white/90 transition-colors"
            >
              {campaign.title}
            </h3>
            <p className="text-xs text-white/35 flex items-center gap-1.5">
              <Icon
                name="Building2"
                size={11}
                className="flex-shrink-0"
              />
              <span className="truncate">{displayBrandName}</span>
              {(isAgencyCampaign || campaign.isAgencyCampaign) &&
                campaign.agencyName && (
                  <>
                    <span className="text-white/20">•</span>
                    <Icon
                      name="Building"
                      size={11}
                      className="flex-shrink-0 text-orange-400"
                    />
                    <span className="truncate text-orange-300/80">
                      {campaign.agencyName}
                    </span>
                  </>
                )}
            </p>
          </div>
        </div>

        {/* Pills row */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className="px-2.5 py-1 bg-white/[0.06] text-white/55
              text-[11px] font-medium rounded-lg border
              border-white/[0.08] flex items-center gap-1.5"
          >
            <Icon name="Tag" size={10} className="text-white/30" />
            {campaign.category}
          </span>

          {(isAgencyCampaign || campaign.isAgencyCampaign) && (
            <span
              className="px-2.5 py-1 bg-orange-500/10 text-orange-300
                text-[11px] font-medium rounded-lg border
                border-orange-500/20 flex items-center gap-1.5"
            >
              <Icon name="Building" size={10} />
              Agency
            </span>
          )}
          {campaign.campaignMode === 'barter' && (
            <span
              className="px-2.5 py-1 bg-violet-500/10 text-violet-300
                text-[11px] font-medium rounded-lg border
                border-violet-500/20 flex items-center gap-1.5"
            >
              <Icon name="Gift" size={10} />
              Barter
            </span>
          )}
          {isExternalCampaign && (
            <span
              className="px-2.5 py-1 bg-blue-500/10 text-blue-300
                text-[11px] font-medium rounded-lg border
                border-blue-500/20 flex items-center gap-1.5"
            >
              <Icon name="ExternalLink" size={10} />
              External
            </span>
          )}
          {campaign.productValue && (
            <span
              className="px-2.5 py-1 bg-green-500/10 text-green-300
                text-[11px] font-medium rounded-lg border
                border-green-500/20 flex items-center gap-1.5"
            >
              <Icon name="Package" size={10} />
              ₹{Number(campaign.productValue).toLocaleString()}
            </span>
          )}
          {typeof campaign.budget === 'number' && (
            <span
              className="px-2.5 py-1 bg-emerald-500/10 text-emerald-300
                text-[11px] font-medium rounded-lg border
                border-emerald-500/20 flex items-center gap-1.5"
            >
              <Icon name="Wallet" size={10} />
              {currencySymbol}
              {formatCurrency(Number(campaign.budget), currency, {
                showSymbol: false,
                decimals: 0,
              })}
            </span>
          )}
          <span
            className="px-2.5 py-1 bg-white/[0.05] text-white/40
              text-[11px] font-medium rounded-lg border
              border-white/[0.07] flex items-center gap-1.5"
          >
            <Icon name="Users" size={10} className="text-white/25" />
            <span className="text-white/70">
              {campaign.applicationsCount}
            </span>{' '}
            applied
          </span>
          {isUrgent && (
            <span
              className="px-2.5 py-1 bg-amber-500/10 text-amber-300
                text-[11px] font-medium rounded-lg border
                border-amber-500/20 flex items-center gap-1.5
                animate-pulse"
            >
              <Icon name="AlertCircle" size={10} />
              Urgent
            </span>
          )}
        </div>

        {/* Description */}
        <p className="text-xs leading-relaxed line-clamp-2 text-white/40 flex-none">
          {campaign.description}
        </p>

        {/* Earnings & Deadline — glass mini-cards */}
        <div className="grid grid-cols-2 gap-2.5 pt-3 border-t border-white/[0.06] mt-auto">
          {/* Earnings */}
          <div
            className="space-y-1.5 bg-white/[0.04] rounded-xl p-3
              border border-white/[0.06]"
          >
            {campaign.campaignMode === 'barter' ? (
              <>
                <p className="text-[9px] text-white/30 font-medium uppercase tracking-wider">
                  Campaign Type
                </p>
                <div className="flex items-center gap-1.5">
                  <Icon
                    name="Gift"
                    size={14}
                    className="text-violet-400"
                  />
                  <p className="text-sm font-bold text-white">
                    Barter
                  </p>
                </div>
                <p className="text-[9px] text-white/25 font-medium">
                  Product collab
                </p>
              </>
            ) : (
              <>
                <p className="text-[9px] text-white/30 font-medium uppercase tracking-wider flex items-center gap-0.5">
                  <Icon name="TrendingUp" size={9} />
                  Earnings
                </p>
                {(minViews > 0 || maxViews > 0) && payPerView > 0 ? (
                  <>
                    <p className="text-sm font-bold text-white leading-none">
                      {currencySymbol}
                      {estimatedMin}–{currencySymbol}
                      {estimatedMax}
                    </p>
                    <p className="text-[9px] text-emerald-400/70 font-medium">
                      {currencySymbol}
                      {payPerView.toFixed(2)}/view
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-white/50">
                      To be decided
                    </p>
                    <p className="text-[9px] text-white/25 font-medium">
                      Contact brand
                    </p>
                  </>
                )}
              </>
            )}
          </div>

          {/* Deadline */}
          <div
            className="space-y-1.5 bg-white/[0.04] rounded-xl p-3
              border border-white/[0.06]"
          >
            <p className="text-[9px] text-white/30 font-medium uppercase tracking-wider flex items-center gap-0.5">
              <Icon name="Clock" size={9} />
              Deadline
            </p>
            <p className="text-sm font-bold text-white leading-none">
              {formattedDate}
            </p>
            <p
              className={`text-[9px] font-medium ${
                isUrgent ? 'text-amber-400' : 'text-white/25'
              }`}
            >
              {daysLeft > 0 ? `${daysLeft} days left` : 'Expired'}
            </p>
          </div>
        </div>

        {/* CTA */}
        {!campaign.isApplied && campaign.status === 'open' && (
          <div className="pt-1 space-y-1.5">
            {isExternalCampaign && onApplyExternal ? (
              <Button
                variant="default"
                fullWidth
                onClick={() => onApplyExternal(campaign)}
                loading={isLoading}
                iconName="ExternalLink"
                iconPosition="right"
                aria-label={`Apply to ${campaign.title} (External)`}
                className="bg-gradient-to-r from-blue-500 to-violet-500
                  text-white hover:from-blue-600 hover:to-violet-600
                  py-2.5 font-semibold text-sm rounded-xl shadow-lg
                  shadow-blue-500/20 transition-all duration-200"
              >
                Apply via NoonX
              </Button>
            ) : (
              <Button
                variant="default"
                fullWidth
                onClick={() => onApply && onApply(campaign.id)}
                loading={isLoading}
                iconName="Send"
                iconPosition="right"
                aria-label={`Apply to ${campaign.title}`}
                className="bg-gradient-to-r from-primary to-accent
                  text-white hover:opacity-90
                  py-2.5 font-semibold text-sm rounded-xl shadow-lg
                  shadow-primary/25 transition-all duration-200"
              >
                Apply Now
              </Button>
            )}
            {isExternalCampaign && (
              <p className="text-[10px] text-center text-white/25">
                Your details will be pre-filled • Powered by NoonX
              </p>
            )}
            {onViewDetails && (
              <button
                type="button"
                onClick={() => onViewDetails(campaign)}
                className="w-full text-center text-[11px] text-white/30
                  hover:text-white/70 transition-all duration-200 py-1.5
                  font-medium hover:bg-white/[0.04] rounded-lg"
              >
                View full details →
              </button>
            )}
          </div>
        )}

        {campaign.isApplied && (
          <div
            className="flex items-center justify-between pt-3
              border-t border-white/[0.06]"
          >
            <p className="text-xs text-white/40 flex items-center gap-1.5">
              <Icon
                name="CheckCircle2"
                size={13}
                className="text-emerald-400"
              />
              Applied
            </p>
            {onViewDetails && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewDetails(campaign)}
                iconName="Eye"
                className="text-xs text-white/40 hover:text-white"
              >
                Details
              </Button>
            )}
          </div>
        )}

        {onViewApplications && (
          <Button
            variant="default"
            fullWidth
            onClick={() => onViewApplications(campaign.id)}
            iconName="Users"
            iconPosition="left"
            iconSize={14}
            className="bg-white/[0.06] hover:bg-white/[0.10] text-white
              py-2.5 font-medium text-sm rounded-xl border
              border-white/[0.08] transition-all"
          >
            View Applications
            {campaign.applicationsCount > 0 && (
              <span
                className="ml-2 px-2 py-0.5 bg-primary/20 text-primary
                  rounded-full text-xs font-semibold border
                  border-primary/20"
              >
                {campaign.applicationsCount}
              </span>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default memo(CampaignCard);
